import {Component, OnDestroy, OnInit} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {EducationService} from '../../../shared/services/education.service';
import {LocaleService} from '../../../shared/services/locale.service';
import {faGraduationCap} from '@fortawesome/free-solid-svg-icons';
import {InterestService} from '../../../shared/services/interest.service';
import {empty, in_array} from '../../../functions';
import {Subscription, of} from 'rxjs';
import {alertMessages, AlertService} from '../../../shared/services/alert.service';
import {ValidationService} from '../../../shared/services/validation.service';
import {catchError} from 'rxjs/operators';
import {DomainService} from '../../../shared/services/domain.service';

import {longMinDate} from '../../../shared/services/time.service';
import { Education } from 'src/app/api/models';
import { SchoolService } from 'src/app/api/services/school.service';
import { School } from 'src/app/api/models/school';
import { SchoolClass } from 'src/app/api/models/school-class';
import { StudentProfileService } from '../../../api/services/student-profile.service';
import { MAX_AREA_OF_INTERESTS } from '../../../shared/utils/student-profile.utils';

@Component({
    selector: 'app-modal-student-profile-education',
    templateUrl: './modal-student-profile-education.component.html',
    styleUrls: ['./modal-student-profile-education.component.scss'],
    standalone: false
})
export class ModalStudentProfileEducationComponent implements OnInit, OnDestroy {

  maxAmount = MAX_AREA_OF_INTERESTS;

  longMinDate = longMinDate;

  educationlevelSubscription: Subscription = new Subscription();

  in_array = in_array;

  faGraduationCap = faGraduationCap;

  // Data container for modal (contains entity data)
  public data:any;

  public profile_id = '';

  public entity:Education = {
    id: undefined,
    schoolId: null,
    educationLevelId: null,
    educationProfileId: null,
    educationProgramId: null,
    schoolName: '',
    schoolCity: '',
    schoolCountry: '',
    programName: '',
    level: '',
    profile: '',
    phase: '',
    description: '',
    extras: '',
    startDate: null,
    endDate: null,
    isCurrent: false,
    graduated: false,
    areaOfInterests: []
  };

  validation:any = {
    validated: false,
    valid: false,
    fields: {
      entity: {
        educationLevelId: {
          regex: 'required',
          error: false
        },
        programName: {
          regex: 'required',
          error: false
        },
        startDate: {
          regex: 'required',
          error: false
        },
        schoolName: {
          regex: 'required',
          error: false
        },
        schoolCity: {
          regex: 'required',
          error: false
        },
        schoolCountry: {
          regex: 'required',
          error: false
        },

      }
    }

  }

  public educationLevels:any = [];
  public profileOptions: any = [];
  public phaseOptions:any = [];

  public schools: School[] = [];
  public schoolClasses: SchoolClass[] = [];
  public useManualSchool = false;

  /**
   * Constructor
   *
   * @param activeModal
   * @param alertService
   * @param educationService
   * @param studentService
   * @param locale
   * @param interestService
   * @param validationService
   * @param domain
   */
  constructor(public activeModal: NgbActiveModal,
              public alertService: AlertService,
              public educationService: EducationService,
              private studentProfileService: StudentProfileService,
              public locale: LocaleService,
              public interestService: InterestService,
              private validationService: ValidationService,
              private domain: DomainService,
              private schoolService: SchoolService) {

  }

  /**
   * Ng on init
   */
  ngOnInit(): void {

    // Fetch education levels
    this.educationlevelSubscription = this.educationService.apiGetEducationLevels(this.domain.getCurrentRegion()).pipe(catchError(this.educationService.handleError('apiGetEducationLevels'))).subscribe((result:any) => {

      if (result) {
        this.educationLevels = result.data;
      }

      this.setEducationLevelOptions();

    });

    if (!empty(this.data)) {

      this.profile_id = this.data.profile_id;
      this.entity = Object.assign({}, this.data.education);

      // If entity already has a schoolId, load classes and stay in select mode
      if (this.entity.schoolId) {
        this.loadSchoolClasses(this.entity.schoolId);
      } else if (this.entity.schoolName) {
        // Has manual school data but no schoolId - start in manual mode
        this.useManualSchool = true;
      }

    }

    this.loadSchools();

  }

  loadSchools() {
    this.schoolService.getSchools({ PageSize: 500 }).subscribe((result: any) => {
      if (result?.data) {
        this.schools = result.data;
      }
    });
  }

  loadSchoolClasses(schoolId: string) {
    this.schoolService.getSchoolClasses({ schoolId }).subscribe((result) => {
      this.schoolClasses = result || [];
    });
  }

  onSchoolChange(schoolId: string) {
    this.entity.schoolId = schoolId || null;
    this.entity.schoolClassId = null;
    this.schoolClasses = [];

    if (schoolId) {
      const school = this.schools.find(s => s.id === schoolId);
      if (school) {
        this.entity.schoolName = school.name;
        this.entity.schoolCity = school.address?.city || '';
        this.entity.schoolCountry = school.address?.country || '';
      }
      this.loadSchoolClasses(schoolId);
    }
  }

  toggleManualSchool() {
    this.useManualSchool = !this.useManualSchool;
    if (this.useManualSchool) {
      this.entity.schoolId = null;
      this.entity.schoolClassId = null;
      this.schoolClasses = [];
    }
  }

  /**
   * Ng on destroy
   */
  ngOnDestroy() {
    this.educationlevelSubscription.unsubscribe();
  }

  /**
   * Set available options for selected education level
   */
  setEducationLevelOptions() {
    if (this.entity.educationLevelId) {

      for (const i in this.educationLevels) {
        if (this.educationLevels.hasOwnProperty(i)) {
          const educationlevel = this.educationLevels[i];

          if (educationlevel['id'] == this.entity.educationLevelId) {

            if (educationlevel.hasOwnProperty('educationProfiles')) {
              this.profileOptions = educationlevel.educationProfiles;
            } else {
              this.profileOptions = [];
            }
            if (educationlevel.hasOwnProperty('phases')) {
              this.phaseOptions = educationlevel.phases;
            } else {
              this.phaseOptions = [];
            }
          }

        }
      }
    }

  }



  /**
   * Show validation error
   *
   * Determine if a validation error needs to be visible to client
   *
   * @param stepNumber
   * @param $entity
   * @param $entityProperty
   */
  showValidationError($entity: string, $entityProperty: string, $property:string = '') {

    if (!empty($property)) {
      if (this.validation['validated'] && this.validation['validated']['fields'][$entity][$entityProperty][$property]['error'] === true) {
        return true;
      } else {
        return false;
      }
    } else {
      if (this.validation['validated'] && this.validation['fields'][$entity][$entityProperty]['error'] === true) {
        return true;
      } else {
        return false;
      }
    }

  }

  /**
   * Validate the registration form
   */
  validateForm() {
    let valid = true;
    const self:any = this;

    // Set entity data selectors
    const $entitySelectors = ['entity'];

    // Check entity columns
    for (const $entitySelector of $entitySelectors) {

      if ( this.validation['fields'].hasOwnProperty($entitySelector) ) {

        for (const key in this.validation['fields'][$entitySelector]) {

          if (!empty(this.validation['fields'][$entitySelector][key])) {

            const entity = this.validation['fields'][$entitySelector][key];

            if (entity.hasOwnProperty('regex') ) {
              const value = self[$entitySelector][key];
              const regPattern = entity['regex'];

              // Set validation flags
              if (!this.validationService.regexValidation(regPattern, value)) {
                this.validation['fields'][$entitySelector][key]['error'] = true;
                valid = false;

              } else {
                this.validation['fields'][$entitySelector][key]['error'] = false;
              }

            }
          }
        }
      }
    }

    // Set validation flags for step
    this.validation['valid'] = valid;
    this.validation['validated'] = true;

    return valid;
  }

  /**
   * Event handler for education program typeahead selection
   *
   * @param $event
   */
  handleEducationProgramSelection($event: any) {

    if ($event.hasOwnProperty('data')) {

      const educationProgramData = Object.assign({}, $event['data']);

      // Map data

      // Set education program data
      if (educationProgramData.hasOwnProperty('id') && educationProgramData.id !== null) {
        this.entity.educationProgramId = educationProgramData.id;
      }
      if (educationProgramData.hasOwnProperty('title') && educationProgramData.title !== null) {
        this.entity.programName = educationProgramData.title;
      }

      // Set education level data
      if (educationProgramData.hasOwnProperty('educationLevel') && educationProgramData.educationLevel !== null) {
        this.entity.educationLevelId = educationProgramData.educationLevel.id;
      }

      // Set school data
      if (educationProgramData.hasOwnProperty('school') && educationProgramData.school !== null) {
        this.entity.schoolName = educationProgramData.school.name;
        this.entity.schoolCity = this.educationService.capitalizeString(educationProgramData.school.address.city);
        this.entity.schoolCountry = educationProgramData.school.address.country;
      }

    }
  }

  /**
   * Save
   *
   * @constructor
   */
  Save() {

    const valid = this.validateForm();

    if (valid) {
      if (empty(this.entity.startDate)) this.entity.startDate = new Date().toISOString();
      if (empty(this.entity.endDate)) this.entity.endDate = new Date().toISOString();

      if (!empty(this.entity.id)) {

        this.studentProfileService.updateEducation({ profileId: this.profile_id, id: this.entity.id!, body: this.entity }).pipe(catchError((err) => { this.alertService.errorWithReport(err, 'studentProfile:saveEducation'); return of(false as const); })).subscribe( (result) => {

          if (result !== false) {
            this.alertService.success(alertMessages.successDataSaved);
            this.activeModal.close(true);
          }

        });

      }
      else {

        const { id, ...body } = this.entity;
        this.studentProfileService.createEducation({ profileId: this.profile_id, body }).pipe(catchError((err) => { this.alertService.errorWithReport(err, 'studentProfile:saveEducation'); return of(false as const); })).subscribe( (result) => {

          if (result !== false) {
            this.alertService.success(alertMessages.successDataSaved);
            this.activeModal.close(true);
          }

        });

      }
    } else {
      this.validationService.goToModalError();
    }

  }

}
